import { useEffect, useRef, useState } from "react";
import {
  capToBalance,
  draftToNumber,
  groupAmount,
  sanitizeAmount,
} from "../utils/format";
import { MIN_ORDER_COST_USDT } from "../constants";

/**
 * Local wager draft + chip handlers. Parent owns committed wager via onWagerChange.
 *
 * @param {object} params
 * @param {number} params.wager
 * @param {(value: number) => void} params.onWagerChange
 * @param {number} params.availableBalance
 */
const formatWithDecimals = (value) => Number(value).toFixed(2);

export const useWagerInput = ({ wager, onWagerChange, availableBalance }) => {
  const [wagerDraft, setWagerDraft] = useState(
    formatWithDecimals(wager > 0 ? wager : MIN_ORDER_COST_USDT),
  );
  // The default applies only until the user sets a wager: their 0 stays 0
  const isWagerSetRef = useRef(false);

  useEffect(() => {
    const capped = capToBalance(wager, availableBalance);

    if (capped === wager) return;

    setWagerDraft(String(capped));
    onWagerChange(capped);
  }, [availableBalance, wager, onWagerChange]);

  // Waits for the balance: the wallets load after the form
  useEffect(() => {
    if (wager > 0 || isWagerSetRef.current || !(availableBalance > 0)) return;

    const initial = capToBalance(MIN_ORDER_COST_USDT, availableBalance);

    setWagerDraft(formatWithDecimals(initial));
    onWagerChange(initial);
  }, [availableBalance, wager, onWagerChange]);

  const commitDraft = (draft) => {
    let next = draft;

    isWagerSetRef.current = true;

    if (next !== "" && next !== "." && Number(next) > availableBalance) {
      next = String(capToBalance(availableBalance, availableBalance));
    }

    setWagerDraft(next);
    onWagerChange(draftToNumber(next));
  };

  const onWagerInput = (event) => {
    commitDraft(sanitizeAmount(event.target.value));
  };

  // An emptied field stays empty while typing and reads 0 after leaving it
  const onWagerBlur = () => {
    if (wagerDraft === "" || wagerDraft === ".") setWagerDraft("0");
  };

  const setWager = (next) => {
    const value = capToBalance(next, availableBalance);

    isWagerSetRef.current = true;
    setWagerDraft(String(value));
    onWagerChange(value);
  };

  return {
    wagerDraft,
    displayWager: groupAmount(wagerDraft),
    onWagerInput,
    onWagerBlur,
    setWager,
    onHalf: () => setWager(wager / 2),
    onDouble: () => setWager(wager * 2),
    onMax: () => setWager(availableBalance),
  };
};
